import { AsyncCombobox, Field, Input } from "@hulahoop/ui";
import type { ReactNode } from "react";
import { QuotaCapacityMeter, type QuotaCapacity } from "../entity/QuotaCapacityMeter";
import type { CreateInvestmentLabels } from "@/domain/types/investmentPiece";
import type { InvestmentState } from "@/domain/investment/types";
import type { Option } from "@/domain/types/option";
import { MONEY_DECIMAL_INPUT, PERCENT_DECIMAL_INPUT } from "@/utils/formatUtils";
import { DecimalUnitInput } from "../forms/DecimalUnitInput";
import { OptionSelect } from "../forms/OptionSelect";
import { InvestmentStateLabel } from "../entity/InvestmentStateLabel";

export interface CreateInvestmentValues {
  beneficiaryUuid: string;
  quotaUuid: string | null;
  investorUuid: string;
  amount: number | null;
  returnPercentage: number | null;
  state: InvestmentState | null;
}

const toCanonical = (value: number | null) => (value === null ? null : String(value));
const fromCanonical = (canonical: string) => (canonical === "" ? null : Number(canonical));

export interface CreateInvestmentSearch {
  (query: string): Promise<readonly Option[]>;
}

export function CreateInvestmentFields({
  values,
  locked,
  quotas,
  states,
  quota,
  transferredDeduction,
  amountError,
  returnError,
  warning,
  format,
  labels,
  searchBeneficiaries,
  searchInvestors,
  onChange,
}: {
  values: CreateInvestmentValues;
  locked: { beneficiary?: string; investor?: string };
  quotas: readonly Option[];
  states: readonly Option<InvestmentState>[];
  quota: QuotaCapacity | null;
  transferredDeduction: number;
  amountError?: string;
  returnError?: string;
  warning?: ReactNode;
  format: (value: number) => string;
  labels: CreateInvestmentLabels;
  searchBeneficiaries: CreateInvestmentSearch;
  searchInvestors: CreateInvestmentSearch;
  onChange: (changes: Partial<CreateInvestmentValues>) => void;
}) {
  return (
    <>
      <Field label={labels.beneficiary} required>
        {locked.beneficiary ? (
          <Input value={locked.beneficiary} disabled readOnly />
        ) : (
          <AsyncCombobox
            loadOptions={searchBeneficiaries}
            value={values.beneficiaryUuid}
            onValueChange={(uuid) => onChange({ beneficiaryUuid: uuid, quotaUuid: null })}
            placeholder={labels.beneficiaryPlaceholder}
            searchPlaceholder={labels.beneficiarySearch}
          />
        )}
      </Field>
      <Field
        label={labels.quota}
        required
        hint={
          !values.beneficiaryUuid && !locked.beneficiary
            ? labels.quotaNoBeneficiary
            : quotas.length === 0
              ? labels.quotaEmpty
              : undefined
        }
      >
        <OptionSelect
          value={values.quotaUuid}
          onValueChange={(uuid) => onChange({ quotaUuid: uuid })}
          placeholder={labels.quotaPlaceholder}
          disabled={quotas.length === 0}
          options={quotas}
          label={labels.quota}
        />
      </Field>
      <Field label={labels.investor} required>
        {locked.investor ? (
          <Input value={locked.investor} disabled readOnly />
        ) : (
          <AsyncCombobox
            loadOptions={searchInvestors}
            value={values.investorUuid}
            onValueChange={(uuid) => onChange({ investorUuid: uuid })}
            placeholder={labels.investorPlaceholder}
            searchPlaceholder={labels.investorSearch}
          />
        )}
      </Field>
      <Field
        label={labels.amount}
        required
        hint={
          quota?.available && quota.total !== null && !amountError
            ? labels.available(format(Number(quota.available)), format(quota.total))
            : undefined
        }
        error={amountError}
      >
        <div className="space-y-1.5">
          <DecimalUnitInput
            value={toCanonical(values.amount)}
            onValueChange={(amount) => onChange({ amount: fromCanonical(amount) })}
            label={labels.amount}
            placeholder="0"
            min="0"
            step="500"
            largeStep="5000"
            {...MONEY_DECIMAL_INPUT}
          />
          {quota && (
            <QuotaCapacityMeter
              quota={quota}
              amount={values.amount ?? 0}
              transferredDeduction={transferredDeduction}
              format={format}
              labels={labels.meter}
            />
          )}
        </div>
      </Field>
      {warning}
      <div className="grid gap-(--card-gap) sm:grid-cols-2">
        <Field label={labels.returnPercentage} required hint={labels.returnCap} error={returnError}>
          <DecimalUnitInput
            value={toCanonical(values.returnPercentage)}
            onValueChange={(percentage) =>
              onChange({ returnPercentage: fromCanonical(percentage) })
            }
            label={labels.returnPercentage}
            placeholder="0"
            min="0"
            step="0.5"
            largeStep="5"
            {...PERCENT_DECIMAL_INPUT}
          />
        </Field>
        <Field label={labels.state} required>
          <OptionSelect
            value={values.state}
            onValueChange={(state) => onChange({ state })}
            options={states}
            label={labels.state}
            renderOption={(option) => (
              <InvestmentStateLabel state={option.value}>{option.label}</InvestmentStateLabel>
            )}
          />
        </Field>
      </div>
    </>
  );
}
